import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IDentalChart, IPerioChart } from '@dataTypes/chartingFeature.types'
import { EmptyDentalChart, EmptyPerioChart } from '@constants/Charting'
import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import {
  fetchChartingbyPatientId,
  addPerioChart,
  deleteDentalChart,
  addDentalChart,
  addTreatmentPlan,
  deleteTreatmentPlan,
  updateTreatmentPlan,
  deletePerioChart,
  updateDentalChart,
  updatePerioChart,
  fetchAllProcedureSettings,
} from '@store/actions'
import { ICommonProcedure } from '@dataTypes/appointment.type'
import { BundledProcedure } from '@dataTypes/bundledProcedure.type'
import { NoteContextType, INote } from '@dataTypes/note.type'
import { ProcedureSettingsType } from '@dataTypes/procedureSettings.type'
import { addNote, updateNote, removeNote } from '@store/actions/note.action'
import { reconcileSavedPerioChart } from '@utils/chart-save.utils'

interface ChartingState {
  perioCharts: IPerioChart[]
  dentalCharts: IDentalChart[]
  treatmentPlans: ITreatmentPlan[]
  selectedPerioChart: IPerioChart | null
  selectedDentalChart: IDentalChart | null
  createPerioChart: IPerioChart
  createDentalChart: IDentalChart
  compareCharts: {
    perioChartA: IPerioChart
    perioChartB: IPerioChart
  } | null
  isDentalUpdated: boolean
  isPerioUpdated: boolean
  isPerioLocked: boolean
  preferCreateDentalChart: boolean
  preferCreatePerioChart: boolean
  loading: boolean
  error: string | null
  latestUpdatedTreatmentPlanId?: string
  procedureSettings: ProcedureSettingsType[]
  bundledProcedures: BundledProcedure[]
  commonProcedures: ICommonProcedure[]
  txPlanProcedureCatalogFetched: {
    settings: boolean
    bundled: boolean
    common: boolean
  }
  latestRequestedPatientId: string | null
}

const sortByCreatedAtDesc = <T extends { date_time_created_at?: string }>(
  items: T[],
): T[] => {
  return items.slice().sort((a: T, b: T): number => {
    const dateA = a.date_time_created_at
      ? new Date(a.date_time_created_at).getTime()
      : 0
    const dateB = b.date_time_created_at
      ? new Date(b.date_time_created_at).getTime()
      : 0
    return dateB - dateA
  })
}

const sortByCreatedAtAsc = <T extends { date_time_created_at?: string }>(
  items: T[],
): T[] => {
  return items.slice().sort((a: T, b: T): number => {
    const dateA = a.date_time_created_at
      ? new Date(a.date_time_created_at).getTime()
      : 0
    const dateB = b.date_time_created_at
      ? new Date(b.date_time_created_at).getTime()
      : 0
    return dateA - dateB
  })
}

const initialState: ChartingState = {
  perioCharts: [],
  dentalCharts: [],
  treatmentPlans: [],
  selectedPerioChart: null,
  selectedDentalChart: null,
  createPerioChart: EmptyPerioChart,
  createDentalChart: EmptyDentalChart,
  compareCharts: null,
  isDentalUpdated: false,
  isPerioUpdated: false,
  isPerioLocked: false,
  preferCreateDentalChart: false,
  preferCreatePerioChart: false,
  loading: false,
  error: null,
  latestUpdatedTreatmentPlanId: undefined,
  procedureSettings: [],
  bundledProcedures: [],
  commonProcedures: [],
  txPlanProcedureCatalogFetched: {
    settings: false,
    bundled: false,
    common: false,
  },
  latestRequestedPatientId: null,
}

const chartingSlice = createSlice({
  name: 'charting',
  initialState,
  reducers: {
    setCompareCharts(
      state,
      action: PayloadAction<{
        chartA: IPerioChart
        chartB: IPerioChart
      } | null>,
    ) {
      if (action.payload) {
        state.compareCharts = {
          perioChartA: action.payload.chartA,
          perioChartB: action.payload.chartB,
        }
      } else {
        state.compareCharts = null
      }
    },
    deleteChartNote(
      state,
      action: PayloadAction<{ id: string; source: NoteContextType | string }>,
    ) {
      if (action.payload.source === NoteContextType.CHARTING) {
        state.dentalCharts = state.dentalCharts.map((chart) => {
          return {
            ...chart,
            notes: chart.notes?.filter(
              (note) => note.note_id !== action.payload.id,
            ),
          }
        })
      }
      if (action.payload.source === NoteContextType.PERIO) {
        state.perioCharts = state.perioCharts.map((chart) => {
          return {
            ...chart,
            notes: chart.notes?.filter(
              (note) => note.note_id !== action.payload.id,
            ),
          }
        })
      }
    },
    setSelectedPerioChart(state, action: PayloadAction<IPerioChart | null>) {
      state.selectedPerioChart = action.payload
    },
    setSelectedDentalChart(state, action: PayloadAction<IDentalChart | null>) {
      state.selectedDentalChart = action.payload
    },
    setCreatePerioChart(state, action: PayloadAction<IPerioChart>) {
      state.createPerioChart = action.payload
    },
    setCreateDentalChart(state, action: PayloadAction<IDentalChart>) {
      state.createDentalChart = action.payload
    },
    setPreferCreateDentalChart(state, action: PayloadAction<boolean>) {
      state.preferCreateDentalChart = action.payload
    },
    setPreferCreatePerioChart(state, action: PayloadAction<boolean>) {
      state.preferCreatePerioChart = action.payload
    },
    setDentalUpdated(state, action: PayloadAction<boolean>) {
      state.isDentalUpdated = action.payload
    },
    setPerioUpdated(state, action: PayloadAction<boolean>) {
      state.isPerioUpdated = action.payload
    },
    reconcilePerioChartSave(
      state,
      action: PayloadAction<{
        request: IPerioChart
        server: IPerioChart
        target: 'create' | 'selected'
        keepEditingSession: boolean
        resetPatientId: string
        resetClinicId: string
      }>,
    ) {
      const {
        request,
        server,
        target,
        keepEditingSession,
        resetPatientId,
        resetClinicId,
      } = action.payload
      const latest =
        target === 'selected'
          ? (state.selectedPerioChart ?? request)
          : state.createPerioChart
      const { chart, hasConcurrentChanges } = reconcileSavedPerioChart(
        request,
        latest,
        server,
      )
      const chartIndex = state.perioCharts.findIndex(
        (entry) => entry.chart_id === server.chart_id,
      )
      if (chartIndex === -1) {
        state.perioCharts.push(server)
        state.perioCharts = sortByCreatedAtDesc(state.perioCharts)
      } else {
        state.perioCharts[chartIndex] = server
      }

      if (keepEditingSession || hasConcurrentChanges) {
        if (target === 'selected') {
          state.selectedPerioChart = chart
        } else {
          state.createPerioChart = chart
          state.preferCreatePerioChart = false
        }
        state.isPerioUpdated = hasConcurrentChanges
        return
      }

      state.selectedPerioChart = chart
      state.createPerioChart = {
        ...EmptyPerioChart,
        patient_id: resetPatientId,
        clinic_id: resetClinicId,
      }
      state.preferCreatePerioChart = true
      state.isPerioUpdated = false
    },
    setPerioLocked(state, action: PayloadAction<boolean>) {
      state.isPerioLocked = action.payload
    },
    clearLatestUpdatedTreatmentPlanId(state) {
      state.latestUpdatedTreatmentPlanId = undefined
    },
    setTxPlanProcedureCatalog(
      state,
      action: PayloadAction<{
        bundledProcedures?: BundledProcedure[]
        commonProcedures?: ICommonProcedure[]
      }>,
    ) {
      if (action.payload.bundledProcedures !== undefined) {
        state.bundledProcedures = action.payload.bundledProcedures
        state.txPlanProcedureCatalogFetched.bundled = true
      }
      if (action.payload.commonProcedures !== undefined) {
        state.commonProcedures = action.payload.commonProcedures
        state.txPlanProcedureCatalogFetched.common = true
      }
    },
    upsertDentalChartInList(state, action: PayloadAction<IDentalChart>) {
      const index = state.dentalCharts.findIndex(
        (chart) => chart.chart_id === action.payload.chart_id,
      )
      if (index !== -1) {
        state.dentalCharts[index] = action.payload
      }
    },
    upsertPerioChartInList(state, action: PayloadAction<IPerioChart>) {
      const index = state.perioCharts.findIndex(
        (chart) => chart.chart_id === action.payload.chart_id,
      )
      if (index !== -1) {
        state.perioCharts[index] = action.payload
      }
    },
    resetDentalChart(state) {
      state.selectedDentalChart = null
      state.createDentalChart = {
        ...EmptyDentalChart,
        patient_id: state.createDentalChart.patient_id,
      }
      state.preferCreateDentalChart = true
      state.isDentalUpdated = false
    },
    resetPerioChart(state) {
      state.selectedPerioChart = null
      state.createPerioChart = {
        ...EmptyPerioChart,
        patient_id: state.createPerioChart.patient_id,
      }
      state.preferCreatePerioChart = true
      state.isPerioUpdated = false
    },
    clearAllChartingData(state) {
      state.perioCharts = []
      state.dentalCharts = []
      state.treatmentPlans = []
      state.selectedPerioChart = null
      state.selectedDentalChart = null
      state.createPerioChart = EmptyPerioChart
      state.createDentalChart = EmptyDentalChart
      state.compareCharts = null
      state.isDentalUpdated = false
      state.isPerioUpdated = false
      state.preferCreateDentalChart = false
      state.preferCreatePerioChart = false
      state.loading = false
      state.error = null
      state.latestUpdatedTreatmentPlanId = undefined
      state.latestRequestedPatientId = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChartingbyPatientId.pending, (state, action) => {
        const isSamePatientRefresh =
          state.latestRequestedPatientId === action.meta.arg &&
          state.treatmentPlans.length > 0
        state.latestRequestedPatientId = action.meta.arg
        state.perioCharts = []
        state.dentalCharts = []
        if (!isSamePatientRefresh) {
          state.treatmentPlans = []
        }
        state.selectedPerioChart = null
        state.selectedDentalChart = null
        state.loading = true
        state.error = null
      })
      .addCase(fetchChartingbyPatientId.fulfilled, (state, action) => {
        if (action.meta.arg !== state.latestRequestedPatientId) {
          return
        }
        state.perioCharts = sortByCreatedAtDesc(action.payload.perio_charts)
        state.dentalCharts = sortByCreatedAtDesc(action.payload.dental_charts)
        state.treatmentPlans = sortByCreatedAtAsc(
          action.payload.treatment_plans,
        )
        state.loading = false
      })
      .addCase(fetchChartingbyPatientId.rejected, (state, action) => {
        if (action.meta.arg !== state.latestRequestedPatientId) {
          return
        }
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(addPerioChart.fulfilled, (state, action) => {
        state.perioCharts.push(action.payload)
        state.perioCharts = sortByCreatedAtDesc(state.perioCharts)
        state.preferCreatePerioChart = false
        if (action.meta.arg.saveSource !== 'autosave') {
          state.selectedPerioChart = action.payload
        }
      })
      .addCase(updatePerioChart.fulfilled, (state, action) => {
        const index = state.perioCharts.findIndex(
          (p) => p.chart_id === action.payload.chart_id,
        )
        if (index !== -1) state.perioCharts[index] = action.payload
      })
      .addCase(addDentalChart.fulfilled, (state, action) => {
        state.dentalCharts.push(action.payload)
        state.dentalCharts = sortByCreatedAtDesc(state.dentalCharts)
        state.preferCreateDentalChart = false
        if (action.meta.arg.saveSource !== 'autosave') {
          state.selectedDentalChart = action.payload
        }
      })
      .addCase(updateDentalChart.fulfilled, (state, action) => {
        const index = state.dentalCharts.findIndex(
          (p) => p.chart_id === action.payload.chart_id,
        )
        if (index !== -1) state.dentalCharts[index] = action.payload
        // Also update selectedDentalChart if it matches the updated chart
        if (state.selectedDentalChart?.chart_id === action.payload.chart_id) {
          state.selectedDentalChart = action.payload
        }
      })
      .addCase(deleteDentalChart.fulfilled, (state, action) => {
        state.dentalCharts = state.dentalCharts.filter(
          (p) => p.chart_id !== action.payload,
        )
        if (state.selectedDentalChart?.chart_id === action.payload) {
          state.selectedDentalChart = null
        }
        if (state.createDentalChart.chart_id === action.payload) {
          state.createDentalChart = {
            ...EmptyDentalChart,
            patient_id: state.createDentalChart.patient_id,
          }
        }
      })
      .addCase(deletePerioChart.fulfilled, (state, action) => {
        state.perioCharts = state.perioCharts.filter(
          (p) => p.chart_id !== action.payload,
        )
        if (state.selectedPerioChart?.chart_id === action.payload) {
          state.selectedPerioChart = null
        }
        if (state.createPerioChart.chart_id === action.payload) {
          state.createPerioChart = {
            ...EmptyPerioChart,
            patient_id: state.createPerioChart.patient_id,
          }
        }
      })
      .addCase(updateTreatmentPlan.fulfilled, (state, action) => {
        const index = state.treatmentPlans.findIndex(
          (p) => p.tp_id === action.payload.tp_id,
        )
        if (index !== -1) state.treatmentPlans[index] = action.payload
      })
      .addCase(deleteTreatmentPlan.fulfilled, (state, action) => {
        state.treatmentPlans = state.treatmentPlans.filter(
          (p) => p.tp_id !== action.payload,
        )
      })
      .addCase(addTreatmentPlan.fulfilled, (state, action) => {
        state.treatmentPlans.push(action.payload)
        state.latestUpdatedTreatmentPlanId = action.payload.tp_id
      })
      .addCase(addNote.fulfilled, (state, action) => {
        const note: INote = action.payload
        if (note.context_type === NoteContextType.CHARTING) {
          const index = state.dentalCharts.findIndex(
            (p) => p.chart_id === note.context_id,
          )
          if (index !== -1) {
            state.dentalCharts[index].notes?.push(note)
          }
        } else if (note.context_type === NoteContextType.PERIO) {
          const index = state.perioCharts.findIndex(
            (p) => p.chart_id === note.context_id,
          )
          if (index !== -1) {
            state.perioCharts[index].notes?.push(note)
          }
        } else if (note.context_type === NoteContextType.TREATMENT_PLAN) {
          state.treatmentPlans = state.treatmentPlans.map((p) =>
            p.tp_id === note.context_id
              ? {
                  ...p,
                  notes: [...(p.notes || []), note],
                }
              : p,
          )
          state.latestUpdatedTreatmentPlanId = note.context_id ?? undefined
        }
      })
      .addCase(fetchAllProcedureSettings.fulfilled, (state, action) => {
        state.procedureSettings = action.payload
        state.txPlanProcedureCatalogFetched.settings = true
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        const note: INote = action.payload
        if (note.context_type === NoteContextType.CHARTING) {
          const chartIndex = state.dentalCharts.findIndex(
            (p) => p.chart_id === note.context_id,
          )
          if (chartIndex !== -1 && state.dentalCharts[chartIndex].notes) {
            state.dentalCharts[chartIndex].notes = state.dentalCharts[
              chartIndex
            ].notes!.map((n) => (n.note_id === note.note_id ? note : n))
          }
        } else if (note.context_type === NoteContextType.PERIO) {
          const chartIndex = state.perioCharts.findIndex(
            (p) => p.chart_id === note.context_id,
          )
          if (chartIndex !== -1 && state.perioCharts[chartIndex].notes) {
            state.perioCharts[chartIndex].notes = state.perioCharts[
              chartIndex
            ].notes!.map((n) => (n.note_id === note.note_id ? note : n))
          }
        } else if (note.context_type === NoteContextType.TREATMENT_PLAN) {
          const planIndex = state.treatmentPlans.findIndex((p) =>
            p.notes?.find((n) => n.note_id === note.note_id),
          )
          if (planIndex !== -1 && state.treatmentPlans[planIndex].notes) {
            state.treatmentPlans[planIndex].notes = state.treatmentPlans[
              planIndex
            ].notes!.map((n) => (n.note_id === note.note_id ? note : n))
          }
        }
      })
      .addCase(removeNote.fulfilled, (state, action) => {
        const noteId: string = action.payload
        // Find and remove from dental charts
        state.dentalCharts = state.dentalCharts.map((chart) => ({
          ...chart,
          notes: chart.notes?.filter((note) => note.note_id !== noteId),
        }))
        // Find and remove from perio charts
        state.perioCharts = state.perioCharts.map((chart) => ({
          ...chart,
          notes: chart.notes?.filter((note) => note.note_id !== noteId),
        }))
        // Find and remove from treatment plans
        state.treatmentPlans = state.treatmentPlans.map((plan) => ({
          ...plan,
          notes: plan.notes?.filter((note) => note.note_id !== noteId),
        }))
      })
  },
})

export const {
  setCompareCharts,
  setSelectedPerioChart,
  setCreatePerioChart,
  setCreateDentalChart,
  setPreferCreateDentalChart,
  setPreferCreatePerioChart,
  setDentalUpdated,
  setPerioUpdated,
  reconcilePerioChartSave,
  setPerioLocked,
  setSelectedDentalChart,
  deleteChartNote,
  clearLatestUpdatedTreatmentPlanId,
  setTxPlanProcedureCatalog,
  resetPerioChart,
  resetDentalChart,
  clearAllChartingData,
  upsertDentalChartInList,
  upsertPerioChartInList,
} = chartingSlice.actions
export default chartingSlice.reducer
